import React from "react";
import { Hash, Wallet, CheckCircle, CreditCard, Receipt } from "lucide-react";
import PageHeader, { PageKPIItem, toneFromLegacyClasses } from "components/ui/PageHeader";
import { PageStats, type PageStat } from "components/ui/page";

interface TransactionsHeaderProps {
    totalTransactions?: number;
    totalAmount?: number;
    successRate?: number;
    totalCredits?: number;
    isLoading?: boolean;
    embedded?: boolean;
}

const TransactionsHeader: React.FC<TransactionsHeaderProps> = ({
    totalTransactions = 0,
    totalAmount = 0,
    successRate = 0,
    totalCredits = 0,
    isLoading = false,
    embedded = false,
}) => {
    const kpis: PageKPIItem[] = [
        {
            icon: Hash,
            value: totalTransactions,
            label: "Total Transactions",
            accentColor: "bg-gradient-to-r from-blue-500 to-indigo-500",
            accentHex: "#6366f1",
            iconBg: "bg-blue-50 dark:bg-blue-900/20",
            iconColor: "text-blue-600 dark:text-blue-400",
        },
        {
            icon: Wallet,
            value: `$${totalAmount.toFixed(2)}`,
            label: "Total Amount Spent",
            accentColor: "bg-gradient-to-r from-emerald-400 to-teal-500",
            accentHex: "#10b981",
            iconBg: "bg-emerald-50 dark:bg-emerald-900/20",
            iconColor: "text-emerald-600 dark:text-emerald-400",
        },
        {
            icon: CheckCircle,
            value: `${successRate}%`,
            label: "Success Rate",
            accentColor: "bg-gradient-to-r from-violet-500 to-purple-500",
            accentHex: "#8b5cf6",
            iconBg: "bg-violet-50 dark:bg-violet-900/20",
            iconColor: "text-violet-600 dark:text-violet-400",
        },
        {
            icon: CreditCard,
            value: totalCredits.toLocaleString(),
            label: "Credits Purchased",
            accentColor: "bg-gradient-to-r from-amber-400 to-orange-500",
            accentHex: "#f59e0b",
            iconBg: "bg-amber-50 dark:bg-amber-900/20",
            iconColor: "text-amber-600 dark:text-amber-400",
        },
    ];

    if (embedded) {
        const stats: PageStat[] = kpis.map((k, i) => ({
            id: String(i),
            icon: k.icon,
            label: k.label,
            value: k.value as React.ReactNode,
            tone: toneFromLegacyClasses(k.accentColor, k.iconBg, k.iconColor),
        }));
        return <PageStats stats={stats} isLoading={isLoading} />;
    }

    return (
        <PageHeader
            icon={<Receipt className="w-5 h-5" />}
            iconBg="bg-blue-50 dark:bg-blue-900/20"
            iconColor="text-blue-600 dark:text-blue-400"
            accentColor="bg-gradient-to-r from-blue-500 to-indigo-500"
            title="Transaction History"
            subtitle="View and download your payment transaction history"
            kpis={kpis}
            isLoading={isLoading}
        />
    );
};

export default TransactionsHeader;
